import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import {
  Package,
  Clock,
  AlertTriangle,
  CheckCircle2,
  TrendingUp,
  Search,
  ChevronRight,
  Truck,
  Factory,
  FileCheck,
  MoreHorizontal,
  Building2,
  Timer,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  poVisibilityApi,
  PoWithVisibility,
  PoMilestone,
  PoSupplierPerformance,
  LeadTimeAnalysis,
  PoVisibilityDashboard,
  PoMilestoneType,
  PoMilestoneStatus,
  milestoneTypeLabels,
  milestoneStatusColors,
  UpdateMilestoneDto,
} from '@/api/services/po-visibility.api';
import { suppliersApi } from '@/api/services/suppliers.api';

// ==========================================
// Metric Card Component
// ==========================================

interface MetricCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  trend?: { direction: 'up' | 'down' | 'neutral'; value: string };
  loading?: boolean;
}

function MetricCard({ title, value, icon, trend, loading }: MetricCardProps) {
  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <Skeleton className="h-4 w-24 mb-2" />
          <Skeleton className="h-8 w-16" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold mt-1">{value}</p>
            {trend && (
              <p
                className={`text-sm mt-1 ${
                  trend.direction === 'up'
                    ? 'text-emerald-600'
                    : trend.direction === 'down'
                      ? 'text-red-600'
                      : 'text-muted-foreground'
                }`}
              >
                {trend.direction === 'up' ? '↑' : trend.direction === 'down' ? '↓' : ''}
                {trend.value}
              </p>
            )}
          </div>
          <div className="p-3 bg-primary/10 rounded-lg">{icon}</div>
        </div>
      </CardContent>
    </Card>
  );
}

// ==========================================
// Milestone Timeline Component
// ==========================================

interface MilestoneTimelineProps {
  milestones: PoMilestone[];
  onUpdateMilestone: (id: string, data: UpdateMilestoneDto) => void;
}

function MilestoneTimeline({ milestones, onUpdateMilestone }: MilestoneTimelineProps) {
  const getStatusIcon = (status: PoMilestoneStatus) => {
    switch (status) {
      case 'COMPLETED':
        return <CheckCircle2 className="h-5 w-5 text-emerald-600" />;
      case 'IN_PROGRESS':
        return <Clock className="h-5 w-5 text-blue-600 animate-pulse" />;
      case 'DELAYED':
        return <AlertTriangle className="h-5 w-5 text-red-600" />;
      case 'SKIPPED':
        return <ChevronRight className="h-5 w-5 text-slate-400" />;
      default:
        return <div className="h-5 w-5 rounded-full border-2 border-gray-300" />;
    }
  };

  const getMilestoneIcon = (type: PoMilestoneType) => {
    if (type.includes('SHIPPED') || type.includes('TRANSIT') || type.includes('DELIVERED')) {
      return <Truck className="h-4 w-4" />;
    }
    if (type.includes('PRODUCTION')) {
      return <Factory className="h-4 w-4" />;
    }
    if (type.includes('QUALITY') || type.includes('RECEIVED')) {
      return <FileCheck className="h-4 w-4" />;
    }
    return <Package className="h-4 w-4" />;
  };

  return (
    <div className="space-y-4">
      {milestones.map((milestone, index) => (
        <div key={milestone.id} className="flex gap-4">
          <div className="flex flex-col items-center">
            {getStatusIcon(milestone.status)}
            {index < milestones.length - 1 && (
              <div
                className={`w-0.5 h-full mt-2 ${
                  milestone.status === 'COMPLETED' ? 'bg-emerald-300' : 'bg-gray-200'
                }`}
              />
            )}
          </div>
          <div className="flex-1 pb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {getMilestoneIcon(milestone.type)}
                <span className="font-medium">{milestoneTypeLabels[milestone.type]}</span>
                <Badge className={milestoneStatusColors[milestone.status]}>
                  {milestone.status}
                </Badge>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={() =>
                      onUpdateMilestone(milestone.id, { status: 'IN_PROGRESS' })
                    }
                  >
                    Mark In Progress
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() =>
                      onUpdateMilestone(milestone.id, {
                        status: 'COMPLETED',
                        actualDate: new Date().toISOString(),
                      })
                    }
                  >
                    Mark Completed
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => onUpdateMilestone(milestone.id, { status: 'DELAYED' })}
                  >
                    Mark Delayed
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => onUpdateMilestone(milestone.id, { status: 'SKIPPED' })}
                  >
                    Skip Milestone
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <div className="text-sm text-muted-foreground mt-1">
              {milestone.actualDate ? (
                <span>
                  Completed: {format(new Date(milestone.actualDate), 'MMM d, yyyy h:mm a')}
                </span>
              ) : milestone.plannedDate ? (
                <span>Planned: {format(new Date(milestone.plannedDate), 'MMM d, yyyy')}</span>
              ) : (
                <span>No date set</span>
              )}
            </div>
            {milestone.location && (
              <div className="text-sm text-muted-foreground">
                Location: {milestone.location}
              </div>
            )}
            {milestone.notes && (
              <div className="text-sm text-muted-foreground mt-1">{milestone.notes}</div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ==========================================
// Main Component
// ==========================================

export default function POVisibility() {
  // State
  const [dashboard, setDashboard] = useState<PoVisibilityDashboard | null>(null);
  const [poList, setPoList] = useState<PoWithVisibility[]>([]);
  const [suppliers, setSuppliers] = useState<{ id: string; name: string }[]>([]);
  const [supplierPerformance, setSupplierPerformance] = useState<PoSupplierPerformance[]>([]);
  const [leadTimeData, setLeadTimeData] = useState<LeadTimeAnalysis[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [supplierFilter, setSupplierFilter] = useState<string>('all');
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 0 });

  // Selected PO for detail view
  const [selectedPo, setSelectedPo] = useState<PoWithVisibility | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  // Update milestone dialog
  const [updateDialog, setUpdateDialog] = useState<{
    open: boolean;
    milestoneId: string | null;
    notes: string;
  }>({ open: false, milestoneId: null, notes: '' });

  // Fetch dashboard data
  const fetchDashboard = async () => {
    try {
      const response = await poVisibilityApi.getDashboard();
      setDashboard(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    }
  };

  // Fetch PO list
  const fetchPoList = async () => {
    try {
      const params: Record<string, unknown> = {
        page: pagination.page,
        limit: pagination.limit,
      };
      if (searchQuery) params.search = searchQuery;
      if (supplierFilter !== 'all') params.supplierId = supplierFilter;

      const response = await poVisibilityApi.getAll(params);
      setPoList(response.data.data);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Failed to fetch PO list:', error);
    }
  };

  // Fetch suppliers for filter
  const fetchSuppliers = async () => {
    try {
      const response = await suppliersApi.getAll({ limit: 100 });
      setSuppliers(
        response.data.map((s: { id: string; name: string }) => ({ id: s.id, name: s.name })),
      );
    } catch (error) {
      console.error('Failed to fetch suppliers:', error);
    }
  };

  // Fetch supplier performance
  const fetchSupplierPerformance = async () => {
    try {
      const response = await poVisibilityApi.getPoSupplierPerformance({ limit: 20 });
      setSupplierPerformance(response.data.records);
    } catch (error) {
      console.error('Failed to fetch supplier performance:', error);
    }
  };

  // Fetch lead time analysis
  const fetchLeadTimeData = async () => {
    try {
      const response = await poVisibilityApi.getLeadTimeAnalysis({});
      setLeadTimeData(response.data);
    } catch (error) {
      console.error('Failed to fetch lead time data:', error);
    }
  };

  // Initial data load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchDashboard(),
        fetchSuppliers(),
        fetchSupplierPerformance(),
        fetchLeadTimeData(),
      ]);
      setLoading(false);
    };
    loadData();
  }, []);

  // Fetch PO list when filters change
  useEffect(() => {
    fetchPoList();
  }, [searchQuery, supplierFilter, pagination.page]);

  // View PO details
  const handleViewPo = async (po: PoWithVisibility) => {
    try {
      const response = await poVisibilityApi.getPoTimeline(po.id);
      setSelectedPo(response.data);
      setIsDetailOpen(true);
    } catch (error) {
      console.error('Failed to fetch PO details:', error);
      toast.error('Failed to load PO details');
    }
  };

  // Update milestone
  const handleUpdateMilestone = async (milestoneId: string, data: UpdateMilestoneDto) => {
    try {
      await poVisibilityApi.updateMilestone(milestoneId, data);
      toast.success('Milestone updated');

      // Refresh PO details if viewing
      if (selectedPo) {
        const response = await poVisibilityApi.getPoTimeline(selectedPo.id);
        setSelectedPo(response.data);
      }

      // Refresh dashboard
      fetchDashboard();
      fetchPoList();
    } catch (error) {
      console.error('Failed to update milestone:', error);
      toast.error('Failed to update milestone');
    }
  };

  // Get PO status color
  const getPoStatusColor = (status: string) => {
    switch (status) {
      case 'DRAFT':
        return 'bg-gray-100 text-gray-700';
      case 'SUBMITTED':
        return 'bg-blue-100 text-blue-700';
      case 'APPROVED':
        return 'bg-emerald-100 text-emerald-700';
      case 'ORDERED':
        return 'bg-purple-100 text-purple-700';
      case 'PARTIALLY_RECEIVED':
        return 'bg-amber-100 text-amber-700';
      case 'RECEIVED':
        return 'bg-emerald-100 text-emerald-700';
      case 'CANCELLED':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">PO Visibility</h1>
          <p className="text-muted-foreground">
            Track purchase order progress and supplier performance
          </p>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Active POs"
          value={dashboard?.kpis.totalActivePOs ?? 0}
          icon={<Package className="h-5 w-5 text-primary" />}
          loading={loading}
        />
        <MetricCard
          title="Pending Milestones"
          value={dashboard?.kpis.pendingMilestones ?? 0}
          icon={<Clock className="h-5 w-5 text-amber-600" />}
          loading={loading}
        />
        <MetricCard
          title="Delayed"
          value={dashboard?.kpis.delayedMilestones ?? 0}
          icon={<AlertTriangle className="h-5 w-5 text-red-600" />}
          loading={loading}
        />
        <MetricCard
          title="On-Time Rate"
          value={dashboard?.kpis.avgOnTimeRate ? `${dashboard.kpis.avgOnTimeRate}%` : 'N/A'}
          icon={<TrendingUp className="h-5 w-5 text-emerald-600" />}
          loading={loading}
        />
      </div>

      {/* Tabs */}
      <Tabs defaultValue="tracking" className="space-y-4">
        <TabsList>
          <TabsTrigger value="tracking">PO Tracking</TabsTrigger>
          <TabsTrigger value="performance">Supplier Performance</TabsTrigger>
          <TabsTrigger value="leadtime">Lead Time Analysis</TabsTrigger>
        </TabsList>

        {/* PO Tracking Tab */}
        <TabsContent value="tracking" className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search PO number..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={supplierFilter} onValueChange={setSupplierFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Filter by supplier" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Suppliers</SelectItem>
                {suppliers.map((supplier) => (
                  <SelectItem key={supplier.id} value={supplier.id}>
                    {supplier.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* PO Table */}
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>PO Number</TableHead>
                    <TableHead>Supplier</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Progress</TableHead>
                    <TableHead>Current Stage</TableHead>
                    <TableHead>Expected Date</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {poList.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                        No purchase orders found
                      </TableCell>
                    </TableRow>
                  ) : (
                    poList.map((po) => (
                      <TableRow key={po.id}>
                        <TableCell className="font-medium">{po.poNumber}</TableCell>
                        <TableCell>{po.supplier.name}</TableCell>
                        <TableCell>
                          <Badge className={getPoStatusColor(po.status)}>{po.status}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Progress value={po.progress} className="w-20" />
                            <span className="text-sm text-muted-foreground">{po.progress}%</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {po.currentMilestone ? (
                            <span className="text-sm">
                              {milestoneTypeLabels[po.currentMilestone]}
                            </span>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell>
                          {po.expectedDate
                            ? format(new Date(po.expectedDate), 'MMM d, yyyy')
                            : '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          ${po.totalAmount?.toLocaleString() ?? '0'}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewPo(po)}
                          >
                            View Timeline
                            <ChevronRight className="h-4 w-4 ml-1" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
                {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                {pagination.total} results
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page === 1}
                  onClick={() =>
                    setPagination((p) => ({ ...p, page: p.page - 1 }))
                  }
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page === pagination.totalPages}
                  onClick={() =>
                    setPagination((p) => ({ ...p, page: p.page + 1 }))
                  }
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Supplier Performance Tab */}
        <TabsContent value="performance" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Supplier Performance Metrics
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Supplier</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead className="text-center">Total Orders</TableHead>
                    <TableHead className="text-center">On-Time</TableHead>
                    <TableHead className="text-center">Late</TableHead>
                    <TableHead className="text-center">Quality Issues</TableHead>
                    <TableHead className="text-center">On-Time Rate</TableHead>
                    <TableHead className="text-center">Avg Lead Time</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {supplierPerformance.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                        No performance data available
                      </TableCell>
                    </TableRow>
                  ) : (
                    supplierPerformance.map((perf) => {
                      const onTimeRate =
                        perf.onTimeDeliveries + perf.lateDeliveries > 0
                          ? Math.round(
                              (perf.onTimeDeliveries /
                                (perf.onTimeDeliveries + perf.lateDeliveries)) *
                                100,
                            )
                          : null;

                      return (
                        <TableRow key={perf.id}>
                          <TableCell className="font-medium">{perf.supplier.name}</TableCell>
                          <TableCell>
                            {format(new Date(perf.periodMonth), 'MMM yyyy')}
                          </TableCell>
                          <TableCell className="text-center">{perf.totalOrders}</TableCell>
                          <TableCell className="text-center text-emerald-600">
                            {perf.onTimeDeliveries}
                          </TableCell>
                          <TableCell className="text-center text-red-600">
                            {perf.lateDeliveries}
                          </TableCell>
                          <TableCell className="text-center text-amber-600">
                            {perf.qualityIssues}
                          </TableCell>
                          <TableCell className="text-center">
                            {onTimeRate !== null ? (
                              <Badge
                                className={
                                  onTimeRate >= 90
                                    ? 'bg-emerald-100 text-emerald-700'
                                    : onTimeRate >= 70
                                      ? 'bg-amber-100 text-amber-700'
                                      : 'bg-red-100 text-red-700'
                                }
                              >
                                {onTimeRate}%
                              </Badge>
                            ) : (
                              '-'
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            {perf.avgLeadTimeDays
                              ? `${perf.avgLeadTimeDays.toFixed(1)} days`
                              : '-'}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Lead Time Analysis Tab */}
        <TabsContent value="leadtime" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Timer className="h-5 w-5" />
                Lead Time Analysis
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Supplier</TableHead>
                    <TableHead>Item / Category</TableHead>
                    <TableHead className="text-center">Avg Lead Time</TableHead>
                    <TableHead className="text-center">Min</TableHead>
                    <TableHead className="text-center">Max</TableHead>
                    <TableHead className="text-center">Std Dev</TableHead>
                    <TableHead className="text-center">Sample Size</TableHead>
                    <TableHead>Last Updated</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leadTimeData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                        No lead time data available
                      </TableCell>
                    </TableRow>
                  ) : (
                    leadTimeData.map((lt) => (
                      <TableRow key={lt.id}>
                        <TableCell className="font-medium">{lt.supplier.name}</TableCell>
                        <TableCell>
                          {lt.item ? lt.item.name : lt.category || 'All Items'}
                        </TableCell>
                        <TableCell className="text-center font-medium">
                          {lt.avgLeadTimeDays.toFixed(1)} days
                        </TableCell>
                        <TableCell className="text-center text-emerald-600">
                          {lt.minLeadTimeDays} days
                        </TableCell>
                        <TableCell className="text-center text-red-600">
                          {lt.maxLeadTimeDays} days
                        </TableCell>
                        <TableCell className="text-center text-muted-foreground">
                          ±{lt.stdDeviation?.toFixed(1) ?? '0'}
                        </TableCell>
                        <TableCell className="text-center">{lt.sampleSize}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {format(new Date(lt.lastCalculatedAt), 'MMM d, yyyy')}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* PO Detail Sheet */}
      <Sheet open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <SheetContent className="sm:max-w-xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>
              {selectedPo ? `PO ${selectedPo.poNumber}` : 'PO Details'}
            </SheetTitle>
          </SheetHeader>

          {selectedPo && (
            <div className="space-y-6 mt-6">
              {/* PO Info */}
              <Card>
                <CardContent className="p-4 space-y-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Supplier</span>
                    <span className="font-medium">{selectedPo.supplier.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Status</span>
                    <Badge className={getPoStatusColor(selectedPo.status)}>
                      {selectedPo.status}
                    </Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total Amount</span>
                    <span className="font-medium">
                      ${selectedPo.totalAmount?.toLocaleString() ?? '0'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Expected Date</span>
                    <span>
                      {selectedPo.expectedDate
                        ? format(new Date(selectedPo.expectedDate), 'MMM d, yyyy')
                        : '-'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Progress</span>
                    <div className="flex items-center gap-2">
                      <Progress value={selectedPo.progress} className="w-24" />
                      <span>{selectedPo.progress}%</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Milestone Timeline */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Milestone Timeline</h3>
                <MilestoneTimeline
                  milestones={selectedPo.milestones}
                  onUpdateMilestone={handleUpdateMilestone}
                />
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Update Milestone Dialog */}
      <Dialog open={updateDialog.open} onOpenChange={(open) => setUpdateDialog((d) => ({ ...d, open }))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Milestone</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={updateDialog.notes}
                onChange={(e) => setUpdateDialog((d) => ({ ...d, notes: e.target.value }))}
                placeholder="Add notes about this update..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUpdateDialog({ open: false, milestoneId: null, notes: '' })}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (updateDialog.milestoneId) {
                  handleUpdateMilestone(updateDialog.milestoneId, { notes: updateDialog.notes });
                }
                setUpdateDialog({ open: false, milestoneId: null, notes: '' });
              }}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
